import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
//eslint-disable-next-line
import { css } from "styled-components/macro";
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { PrimaryButton } from "components/misc/Buttons.js";

import { ReactComponent as SvgDecoratorBlob3 } from "images/svg-decorator-blob-3.svg";

import productionLineImage from "assets/production_line.png";
import acMotorImage from "assets/motor_ac.png";
import horimetroImage from "assets/horimetro.png";
import temperaturaSensor from "assets/temperature_sensor.png";
import pressureSensor from "assets/pressure_sensor.jpeg";
import nivelSensor from "assets/nivel_sensor.png";
import electricitySensor from "assets/electricity_sensor.png";
import energyAnalyseImage from "assets/epi.png";
import counterImage from "assets/contador.png";

const Container = tw.div`relative`;

const ThreeColumnContainer = styled.div`
  ${tw`flex flex-col items-center md:items-stretch md:flex-row flex-wrap md:justify-center max-w-screen-lg mx-auto py-20 md:py-24`}
`;
const Subheading = tw(SubheadingBase)`mb-4`;
const Heading = tw(SectionHeading)`w-full`;
const Description = tw(SectionDescription)`w-full text-center`;

const VerticalSpacer = tw.div`mt-10 w-full`

const Column = styled.div`
  ${tw`md:w-1/2 lg:w-1/3 max-w-sm`}
`;

const Card = styled.div`
  ${tw`flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left h-full mx-4 px-2 py-8`}
  .imageContainer {
    ${tw`border text-center rounded-full p-5 flex-shrink-0`}
    img {
      ${tw`w-6 h-6`}
    }
  }

  .textContainer {
    ${tw`sm:ml-4 mt-4 sm:mt-2`}
  }

  .title {
    ${tw`mt-4 tracking-wide font-bold text-2xl leading-none`}
  }

  .description {
    ${tw`mt-1 sm:mt-4 font-medium text-secondary-100 leading-loose`}
  }
`;

const DecoratorBlob = styled(SvgDecoratorBlob3)`
  ${tw`pointer-events-none absolute right-0 bottom-0 w-64 opacity-25 transform translate-x-32 translate-y-48 `}
`;

export default ({
  cards = null,
  heading = "Produtos incríveis para a industria",
  subheading = "Produtos",
  description = "Oferecemos diversas soluções na área de automação industrial, trabalhamos com sensores próprios e sensores de terceiros, tudo interligado com Dashboards personalizados, conforme necessidade do cliente ou interligando com Dashboards existentes."
}) => {
  /*
   * This componets has an array of object denoting the cards defined below. Each object in the cards array can have the key (Change it according to your need, you can also add more objects to have more cards in this feature component) or you can directly pass this using the cards prop:
   *  1) imageSrc - the image shown at the top of the card
   *  2) title - the title of the card
   *  3) description - the description of the card
   *  If a key for a particular card is not provided, a default value is used
   */

  const defaultCards = [
    {
      imageSrc: energyAnalyseImage,
      title: "Monitoramento de uso de EPIs",
      description: "Utiliza sistema de CFTV para monitoramento de uso de EPIs. Registra presença e uso de equipamentos de proteção individual, garantindo segurança e conformidade. Relatórios disponíveis para gestão de segurança no trabalho e possibilidade de envio de alertas instantâneos."
    },
    {
      imageSrc: horimetroImage,
      title: "Horímetro",
      description: "Registra o tempo de funcionamento de máquinas e equipamentos, essencial para manutenção preventiva e controle de uso. Emite mensagens de alerta para e-mail ou WhatsApp avisando tempo de utilização."
    },
    {
      imageSrc: productionLineImage,
      title: "Contadores para linha de produção",
      description: "Realiza contagem de peças produzidas, com possibilidade de integração com outros sistemas. Ideal para monitoramento de eficiência e produtividade e visualização de gráfico de produção em Dashboard por período."
    },
    {
      imageSrc: acMotorImage,
      title: "Detector defeito para motores AC",
      description: "Registra vibrações, temperatura e corrente elétrica de motores AC, detectando falhas como desbalanceamento, desalinhamento e superaquecimento. Emite alertas via e-mail ou WhatsApp."
    },
    {
      imageSrc: temperaturaSensor,
      title: "Sensor Temperatura",
      description: "Registra temperatura de ambientes, máquinas e equipamentos, essencial para monitoramento de condições operacionais. Emite mensagens de alerta para e-mail ou WhatsApp quando a temperatura sai de limites pré-definidos. Dashboard para visualização de gráficos de temperatura por período."
    },
    {
      imageSrc: nivelSensor,
      title: "Sensor de nível",
      description: "Medidor de nível de líquidos e sólidos, essencial para controle de estoques e processos industriais. Emite mensagens de alerta para e-mail ou WhatsApp quando o nível atinge limites críticos. Possibilidade de utilizar automação com saídas NA/NF. Histório de níveis em Dashboard, permite visualização de gráfico por período."
    },
    {
      imageSrc: pressureSensor,
      title: "Sensor de pressão",
      description: "Emedidor de pressão para monitoramento de sistemas hidráulicos e pneumáticos. Emite mensagens de alerta para e-mail ou WhatsApp quando a pressão sai de limites pré-definidos. Dashboard para visualização de gráficos de pressão por período."
    },
    {
      imageSrc: electricitySensor,
      title: "Sensor de corrente elétrica",
      description: "Sensor de corrente elétrica AC. Emite mensagens de alerta para e-mail ou WhatsApp quando a corrente sai de limites pré-definidos. Dashboard para visualização de gráficos de corrente por período."
    },
    {
      imageSrc: counterImage,
      title: "Contador de objetos por imagem ou vídeo",
      description: "Realize contagem em instantes usando IA. Oferecemos sistemas para contagem por demanda ou automático, que realizam as contagem continuamente e registra tudo em banco de dados que podem ser acesso via DashBoard."
    }
  ];

  if (!cards) cards = defaultCards;

  return (
    <Container>
      <ThreeColumnContainer>
        {subheading && <Subheading>{subheading}</Subheading>}
        <Heading>{heading}</Heading>
        {description && <Description>{description}</Description>}
        <VerticalSpacer />
        {cards.map((card, i) => (
          <Column key={i}>
            <Card>
              <Stack direction="row" spacing={2}>
                <Avatar
                  sx={{
                    width: 110,
                    height: 110,
                    border: '1px solid #d3d3d3',
                    boxShadow: 50,
                    shadowOpacity: .5,
                    elevation: 5,
                    boxShadow: 7,
                    borderRadius: '50%'
                  }}
                  alt={card.title}
                  src={card.imageSrc}
                />
              </Stack>
              <span className="textContainer">
                <span className="title">{card.title}</span>
                <p className="description">
                  {card.description}
                </p>
              </span>
            </Card>
          </Column>
        ))}
      </ThreeColumnContainer>
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <PrimaryButton
          buttonRounded={true}
          as="a"
          href="https://wa.me/5531984415787?text=Ol%C3%A1%2C%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20os%20produtos%20e%20servi%C3%A7os%20da%20EMS-A."
          target="_blank"
        >
          Entre em contato e solicite uma demonstração
        </PrimaryButton>
      </div>
      <DecoratorBlob />
    </Container>
  );
};
